import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import {
    ASSIGNEE_ASSIGNABLE_LABELS,
    CONVERSATION_ASSIGNEE_PARAM,
    CONVERSATION_FILTER_PARAM,
    CONVERSATION_PATIENT_PARAM,
    CONVERSATION_STATUS_PARAM,
    CONVERSATION_TEAM_PARAM
} from '../utils/conversationsLink';
import { CONVERSATION_DETAIL_KEY } from './queryKeys';
import { fetchConversation } from '../services/conversations';
import type {
    ConversationRow,
    ConversationsParams,
    ConversationStatus
} from '../services/conversations';

type StatusFilter = ConversationsParams['filter'];

interface ConversationsDeepLinkOptions {
    selectedPatient: ConversationRow | null;
    onSelect: (row: ConversationRow) => void;
    /** `missing` false means a transient failure, so the link is worth keeping. */
    onUnavailable: (missing: boolean) => void;
    setAssignee: (labels: string[]) => void;
    setTeam: (teams: string[]) => void;
    /** Clears the filters a link never names — practice and thread visibility. */
    clearScope: () => void;
    setStatus: (status: StatusFilter) => void;
}

function isStatusFilter(value: string | null): value is StatusFilter {
    return value === 'unread' || value === 'seen' || value === 'all';
}

function isNotFound(error: unknown): boolean {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return status === 404;
}

// Applies a link's filters once, and persists them for the session.
function useLinkedFilterSeed(
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    setAssignee: (labels: string[]) => void,
    setTeam: (teams: string[]) => void,
    clearScope: () => void,
    setStatus: (status: StatusFilter) => void
) {
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current) return;
        seededRef.current = true;
        const rawFilter = searchParams.get(CONVERSATION_FILTER_PARAM);
        const filter = isStatusFilter(rawFilter) ? rawFilter : null;
        const assignee = searchParams
            .getAll(CONVERSATION_ASSIGNEE_PARAM)
            .filter((label) => ASSIGNEE_ASSIGNABLE_LABELS.includes(label));
        const team = searchParams.getAll(CONVERSATION_TEAM_PARAM);
        // Every param is validated before it counts as seeding: a stale link
        // must not wipe the user's filters and apply nothing in their place.
        if (!assignee.length && !team.length && !filter) return;
        // A seeding link owns every persisted filter — named ones it sets, the
        // rest it clears — so none can narrow the result below the link.
        setAssignee(assignee);
        setTeam(team);
        clearScope();
        setStatus(filter ?? 'unread');
        const next = new URLSearchParams(searchParams);
        next.delete(CONVERSATION_ASSIGNEE_PARAM);
        next.delete(CONVERSATION_TEAM_PARAM);
        next.delete(CONVERSATION_FILTER_PARAM);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, setAssignee, setTeam, clearScope, setStatus]);
}

// The panel toggle can link a thread with no chat_status row yet (the backend
// 404s); the sibling row still names the patient, so open the linked thread on it.
async function fetchLinkedConversation(
    patientId: string,
    status: ConversationStatus
): Promise<ConversationRow> {
    try {
        return await fetchConversation(patientId, status);
    } catch (err) {
        if (!isNotFound(err)) throw err;
        const sibling = await fetchConversation(
            patientId,
            status === 'internal' ? 'external' : 'internal'
        );
        return { ...sibling, status };
    }
}

// Resolves `?patientId` by id rather than by scanning the list: the target may
// sit behind the user's own sort, page size or filters, and must open anyway.
function useLinkedPatient(
    linkedPatientId: string | null,
    linkedStatus: ConversationStatus,
    selectedPatient: ConversationRow | null,
    onSelect: (row: ConversationRow) => void,
    onUnavailable: (missing: boolean) => void
) {
    // State so the URL sync sees the flip in the same render as the selection;
    // the ref fires once, which state alone can't guarantee under StrictMode.
    const [settled, setSettled] = useState(!linkedPatientId);
    const settledRef = useRef(!linkedPatientId);
    const [missing, setMissing] = useState(false);
    const {
        data: linkedPatient,
        isError,
        error
    } = useQuery({
        queryKey: CONVERSATION_DETAIL_KEY(linkedPatientId ?? '', linkedStatus),
        queryFn: () => fetchLinkedConversation(linkedPatientId ?? '', linkedStatus),
        enabled: Boolean(linkedPatientId) && !settled,
        staleTime: Infinity,
        retry: false
    });

    useEffect(() => {
        if (settledRef.current) return;
        // A row the user clicked first wins over an in-flight link.
        if (selectedPatient) {
            settledRef.current = true;
            setSettled(true);
        } else if (linkedPatient) {
            settledRef.current = true;
            setSettled(true);
            onSelect(linkedPatient);
        } else if (isError) {
            settledRef.current = true;
            setSettled(true);
            const notFound = isNotFound(error);
            setMissing(notFound);
            onUnavailable(notFound);
        }
    }, [linkedPatient, isError, error, selectedPatient, onSelect, onUnavailable]);

    return { settled, keepParam: isError && !missing };
}

// Owns every `?patientId` / `?assignee` / `?status` interaction: seeding the
// filters from a link, opening the panel, and syncing the selection back.
export function useConversationsDeepLink({
    selectedPatient,
    onSelect,
    onUnavailable,
    setAssignee,
    setTeam,
    clearScope,
    setStatus
}: ConversationsDeepLinkOptions) {
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedPatientId = useRef(searchParams.get(CONVERSATION_PATIENT_PARAM)).current;
    // A legacy link without `status` resolves to the external thread.
    const linkedStatus = useRef<ConversationStatus>(
        searchParams.get(CONVERSATION_STATUS_PARAM) === 'internal' ? 'internal' : 'external'
    ).current;

    const { settled, keepParam } = useLinkedPatient(
        linkedPatientId,
        linkedStatus,
        selectedPatient,
        onSelect,
        onUnavailable
    );
    useLinkedFilterSeed(searchParams, setSearchParams, setAssignee, setTeam, clearScope, setStatus);

    // Gated on `settled` so it can't strip a param the pending panel still needs.
    useEffect(() => {
        if (!settled) return;
        const currentParam = searchParams.get(CONVERSATION_PATIENT_PARAM);
        const currentStatus = searchParams.get(CONVERSATION_STATUS_PARAM);
        const selectedId = selectedPatient?.patientId ?? null;
        const selectedStatus = selectedPatient?.status ?? null;
        if (selectedId && (currentParam !== selectedId || currentStatus !== selectedStatus)) {
            const next = new URLSearchParams(searchParams);
            next.set(CONVERSATION_PATIENT_PARAM, selectedId);
            if (selectedStatus) next.set(CONVERSATION_STATUS_PARAM, selectedStatus);
            setSearchParams(next, { replace: true });
        } else if (!selectedId && (currentParam || currentStatus) && !keepParam) {
            const next = new URLSearchParams(searchParams);
            next.delete(CONVERSATION_PATIENT_PARAM);
            next.delete(CONVERSATION_STATUS_PARAM);
            setSearchParams(next, { replace: true });
        }
    }, [settled, keepParam, selectedPatient, searchParams, setSearchParams]);
}
